import { useState, useEffect, useRef, memo } from "react";
import { useContext, useMemo } from "react";
import LevelTransmitterIndicator from "../components/LevelTransmitterIndicator";
import Transmitter from "../components/Transmitter";
import ControlValveControlPanel from "../components/ControlValveControlPanel";
import Valve from "../components/Valve";
import "./Furnace.css";
import { WellContext } from "../context/WellContext";
import { AlarmContext } from "../context/AlarmContext";
import { PumpContext } from "../context/PumpContext";
import { ProcessContext } from "../context/ProcessContext";
import Pump from "../components/Pump";
import PumpFaceplate from "../components/PumpFaceplate";
import MDSFaceplate from "../components/MDSFaceplate";
import { PlantStore } from "../engine/PlantStore";
import { usePlantStore } from "../engine/usePlantStore";


import Damper from "../components/Damper";
import PilotFlame from "../flame/PilotFlame";
import SparkButton from "../flame/SparkButton";
import MainGasFlame from "../flame/MainGasFlame";
import FuelOilFlame from "../flame/FuelOilFlame";
import FlueGas from "../flame/FlueGas";
import StackFlueGas from "../flame/StackFlueGas";

import { trackPageVisit } from "../competency/PageTracker";
import TransmitterFaceplate from "../components/TransmitterFaceplate";



function ValveOutput({
    tag,
    value,
    x,
    y,
    r = 0,
    description = "Control Valve Opening"
}) {

    const [hover, setHover] = useState(false);
    const [showFaceplate, setShowFaceplate] = useState(false);

    return (
        <>
            {/* Hover */}
            {hover && (
                <div
                    style={{
                        position: "absolute",
                        left: x + 30,
                        top: y - 5,
                        background: "rgba(0,80,180,.85)",
                        color: "#fff",
                        padding: "5px 8px",
                        borderRadius: "4px",
                        border: "1px solid #4fc3ff",
                        fontSize: "11px",
                        zIndex: 9999,
                        pointerEvents: "none",
                        whiteSpace: "nowrap"
                    }}
                >
                    <strong>{tag}</strong><br />
                    {description}
                </div>
            )}

            {/* Original Valve Output */}
            <div
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowFaceplate(true)}
                style={{
                    position: "absolute",
                    left: x,
                    top: y,

                    color: "lime",
                    background: "black",

                    fontSize: "10px",
                    padding: "1px 4px",

                    borderRadius: "4px",
                    border: "1px solid #555",

                    transform: `rotate(${r}deg)`,
                    transformOrigin: "center",

                    cursor: "pointer",

                    fontWeight: "bold",

                    minWidth: "22px",
                    textAlign: "center",

                    transition: "0.2s",
                    boxShadow: hover
                        ? "0 0 8px rgba(0,255,255,.7)"
                        : "none"
                }}
            >
                {Number(value).toFixed(1)} %
            </div>

            {showFaceplate && (
                <TransmitterFaceplate
                    tag={tag}
                    value={Number(value)}
                    unit="%"
                    min={0}
                    max={100}
                    HH={100}
                    H={90}
                    L={10}
                    LL={0}
                    description={description}
                    x={x + 35}
                    y={y + 20}
                    onClose={() => setShowFaceplate(false)}
                />
            )}
        </>
    );
}





function Furnace() {

  // ✅ Use ref to track render count
  const renderCount = useRef(0);
  renderCount.current += 1;
  console.log(`Furnace Render: ${renderCount.current}`);

  // ✅ Track page visit only once
  useEffect(() => {
     console.log("Effect A");
    trackPageVisit("Furnace");
  }, []);

    const {processData,  setProcessData} = useContext(ProcessContext);
  const { toggleValve, valves } = useContext(WellContext);
  const [hoveredValve, setHoveredValve] = useState(null);
  const spark = processData.spark ?? false;
  const pilotOn = processData.pilotOn ?? false;
 


  const [airPreheaterTemp, setAirPreheaterTemp] = useState({

    TT_21013:25,
    TT_21014:25

});


  const setSpark = (value) => {
  setProcessData(prev => ({
    ...prev,
    spark: value
  }));
};

const setPilotOn = (value) => {
  setProcessData(prev => ({
    ...prev,
    pilotOn: value
  }));
};

  const DEFAULT_VALVES = {
    FCV_2101: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2102: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2103: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2104: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2105: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2106: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2107: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2108: { mode: "Manual", opening: 0, sp: "", pv: "" },
    FCV_2109: { mode: "Manual", opening: 0, sp: "", pv: "" },
  };

  const [valveData, setValveData] = useState(() => {
    const saved = localStorage.getItem("Furnace_valveData");
    if (!saved) return DEFAULT_VALVES;
    try {
      const parsed = JSON.parse(saved);
      return {
        FCV_2101: { ...DEFAULT_VALVES.FCV_2101, ...parsed.FCV_2101 },
        FCV_2102: { ...DEFAULT_VALVES.FCV_2102, ...parsed.FCV_2102 },
        FCV_2103: { ...DEFAULT_VALVES.FCV_2103, ...parsed.FCV_2103 },
        FCV_2104: { ...DEFAULT_VALVES.FCV_2104, ...parsed.FCV_2104 },
        FCV_2105: { ...DEFAULT_VALVES.FCV_2105, ...parsed.FCV_2105 },
        FCV_2106: { ...DEFAULT_VALVES.FCV_2106, ...parsed.FCV_2106 },
        FCV_2107: { ...DEFAULT_VALVES.FCV_2107, ...parsed.FCV_2107 },
        FCV_2108: { ...DEFAULT_VALVES.FCV_2108, ...parsed.FCV_2108 },
        FCV_2109: { ...DEFAULT_VALVES.FCV_2109, ...parsed.FCV_2109 },
      };
    } catch {
      return DEFAULT_VALVES;
    }
  });

  /* ✅ Persist */
  useEffect(() => {
     console.log("Effect B");
    localStorage.setItem("Furnace_valveData", JSON.stringify(valveData));
  }, [valveData]);

  const updateValveData = (tag, field, value) => {
    setValveData(prev => ({
      ...prev,
      [tag]: {
        ...(prev[tag] || DEFAULT_VALVES[tag]),
        [field]: field === "opening" ? Number(value) || 0 : value
      }
    }));
  };

  /* ================= PANEL CONTROL ================= */
  const [openPanels, setOpenPanels] = useState([]);
  const openPanel = (tag) => {
    if (!openPanels.includes(tag)) {
      setOpenPanels(prev => [...prev, tag]);
    }
  };
  const closePanel = (tag) => {
    setOpenPanels(prev => prev.filter(p => p !== tag));
  };

  // ================= PUMP PANEL LOGIC =================
  const [openPumpPanels, setOpenPumpPanels] = useState([]);
  const openPumpPanel = (tag) => {
    if (!openPumpPanels.includes(tag)) {
      setOpenPumpPanels(prev => [...prev, tag]);
    }
  };
  const closePumpPanel = (tag) => {
    setOpenPumpPanels(prev => prev.filter(p => p !== tag));
  };

  const tagStyle = {
    position: "absolute",
    top: -30,
    left: "50%",
    transform: "translateX(-50%)",
    background: "black",
    color: "#00ffcc",
    fontSize: "13px",
    fontWeight: "bold",
    padding: "3px 8px",
    borderRadius: "4px",
    border: "1px solid #555",
    whiteSpace: "nowrap",
    zIndex: 999
  };

  // ================= AIR AND DAMPER OPENING =================
  const renderInput = ({
    name,
    value,
    setter,
    x,
    y,
    w,
    h,
    bg = "#222",
    color = "#222"
  }) => (
    <input
      value={value}
      onChange={(e) => setter(e.target.value)}
      style={{
        position: "absolute",
        left: x,
        top: y,
        width: w,
        height: h,
        backgroundColor: bg,
        color,
        border: "1px solid #777",
        borderRadius: "4px",
        padding: "1px 2px",
        fontSize: "14px",
        zIndex: 20,
        textAlign: "center"
      }}
    />
  );

  const Damper_Opening =  processData.Damper_Opening ?? 75;
  const ATM_Air =  processData.ATM_Air ?? 25;

  const setDamper_Opening = (value) => {
  setProcessData(prev => ({
    ...prev,
    Damper_Opening: Number(value)
  }));
};

const setATM_Air = (value) => {
  setProcessData(prev => ({
    ...prev,
    ATM_Air: Number(value)
  }));
};

  // ================= RETREIVING OF DATA FROM OTHER PAGES VIA LOCAL STROAGE =================
  const { transmitters } = usePlantStore();
  const TT_2006 = transmitters.TT_2006 || 0;
  const FIC_16005 = transmitters.FIC_16005 || 0;
  const PT_16005 = transmitters.PT_16005 || 0;
  const FIC_16010 = transmitters.FIC_16010 || 0;
  const FIC_16011 = transmitters.FIC_16011 || 0;
  const FIC_18012 = transmitters.FIC_18012 || 0;
  const PT_18004 = transmitters.PT_18004 || 0;
  const FIC_3203 = transmitters.FIC_3203 || 0;
  const PT_3203 = transmitters.PT_3203 || 0;
  const FIC_3206 = transmitters.FIC_3206 || 0;
  const PT_3206 = transmitters.PT_3206 || 0;
  const FIC_3202 = transmitters.FIC_3202 || 0;
  const PT_3202 = transmitters.PT_3202 || 0;
  const mainFlameOn = transmitters.MAIN_FLAME_ON || false;
  const flameOn = transmitters.FLAME_ON || false;

  const xv2101Open = !valves?.XV_2101;
  const xv2102Open = !valves?.XV_2102;
  const xv2103Open = !valves?.XV_2103;
  const xv2104Open = !valves?.XV_2104;
  const xv2105Open = !valves?.XV_2105;
  const xv2106Open = !valves?.XV_2106;
  const xv2107Open = !valves?.XV_2107;
  const xv2108Open = !valves?.XV_2108;
  const xv2109Open = !valves?.XV_2109;
  const xv21010Open = !valves?.XV_21010;

  const PT_2101 = PT_16005 * 0.43;
  const FIC_2101 = FIC_16005;
  const FIC_2102 = FIC_16010;
  const FIC_2103 = FIC_16011;
  const TT_2101 = FIC_2101 > 1 ? TT_2006 : 25;


  const FCV_2101 = (valveData?.FCV_2101?.opening || 0) / 100;
  const FCV_2102 = (valveData?.FCV_2102?.opening || 0) / 100;

  const PT_2102 = xv2105Open ? PT_3203 * ((valveData?.FCV_2107?.opening || 0) / 70) : 0.03;
  const FIC_2104 = xv2105Open ? FIC_3203 * ((valveData?.FCV_2107?.opening || 0) / 70) : 0;

  const PT_2103 = xv2104Open ? PT_3206 * ((valveData?.FCV_2106?.opening || 0) / 70) : 0.03;
  const FIC_2105 = xv2104Open ? FIC_3206 * ((valveData?.FCV_2106?.opening || 0) / 70) : 0;

  const PT_2104 = xv2103Open ? PT_3202 * ((valveData?.FCV_2105?.opening || 0) / 100) : 0.03;
  const FIC_2106 = xv2103Open ? FIC_3202 * ((valveData?.FCV_2105?.opening || 0) / 70) : 0;

  const PT_2105 = xv2102Open ? 0.679 * ((valveData?.FCV_2104?.opening || 0) / 70) : 0.03;
  const FIC_2107 = xv2102Open ? 3.35 * ((valveData?.FCV_2104?.opening || 0) / 70) : 0;

  const PT_2106 = xv2101Open ? 0.979 * ((valveData?.FCV_2103?.opening || 0) / 70) : 0.03;
  const FIC_2108 = xv2101Open ? 6.35 * ((valveData?.FCV_2103?.opening || 0) / 70) : 0; 

  /* =========================================================
     FURNACE BOTTOM TEMPERATURE TT_2102
  ========================================================= */
  const pilotTemperature = spark ? 15.65 : 0;
  const fuelOilPressure = Number(PT_2105) || 0;
  const atomizerPressure = Number(PT_2106) || 0;

  const cngPressure = Number(PT_2104) || 0;
  const methanePressure = Number(PT_2103) || 0;

  const fuelOilFlameON = fuelOilPressure > 0.05;
  const fuelIntensity = Math.max(0, Math.min((fuelOilPressure - 0.05) / (0.970 - 0.05), 1));
  const atomizerFactor = Math.max(0, Math.min(atomizerPressure / 1.299, 1));
  const fuelOilHeatIntensity = fuelOilFlameON ? fuelIntensity * (0.5 + (0.5 * atomizerFactor)) : 0;

  const gasPressure = Math.max(cngPressure, methanePressure);
  const mainGasFlameON = gasPressure > 0.05;
  const gasIntensity = Math.max(0, Math.min((gasPressure - 0.05) / (0.970 - 0.05), 1));
  const mainGasHeatIntensity = mainGasFlameON ? gasIntensity : 0;

  const furnaceHeatIntensity = Math.max(fuelOilHeatIntensity, mainGasHeatIntensity);
  const TT_2102 = furnaceHeatIntensity > 0 && (mainFlameOn || flameOn) ? (25 + (furnaceHeatIntensity * (620 - 25))) + (pilotTemperature*((valveData?.FCV_2107?.opening || 0) / 100)): 25 + (pilotTemperature*((valveData?.FCV_2107?.opening || 0) / 100));

  /* =========================================================
     FURNACE CRUDE OUTLET TT_2105
     ✅ FIXED: Use refs and only create interval ONCE
  ========================================================= */
  const [TT_2105, setTT_2105] = useState(365);

  const TT_2105_intervalRef = useRef(null);
  const TT_2105_isMounted = useRef(false);
  const TT_2105_Ref = useRef(350);

  const TT_2101_Ref = useRef(TT_2101);
  const TT_2102_Ref = useRef(TT_2102);
  const FIC_2101_Ref = useRef(FIC_2101);

  useEffect(() => {
     console.log("Effect C");
    TT_2101_Ref.current = TT_2101;
  }, [TT_2101]);

  useEffect(() => {
     console.log("Effect D");
    TT_2102_Ref.current = TT_2102;
  }, [TT_2102]);

  useEffect(() => {
     console.log("Effect E");
    FIC_2101_Ref.current = FIC_2101;
  }, [FIC_2101]);

  // ✅ Only create TT_2105 interval ONCE
  useEffect(() => {
     console.log("Effect F");
    if (TT_2105_isMounted.current) {
      console.log('⚠️ TT_2105 interval already exists, skipping...');
      return;
    }

    TT_2105_isMounted.current = true;
    console.log('✅ TT_2105 interval created');

    TT_2105_intervalRef.current = setInterval(() => {
      const crudeInlet = TT_2101_Ref.current;
      const furnaceTemp = TT_2102_Ref.current;
      const flow = Math.max(FIC_2101_Ref.current, 0.1);

      let targetTT2105 = crudeInlet;

      if (furnaceTemp < 50) {
        targetTT2105 = crudeInlet;
      } else {
        const designFlow = 20;
        const residenceFactor = Math.pow(designFlow / flow, 0.75);
        const availableHeat = furnaceTemp - crudeInlet;
        let temperatureRise = availableHeat * 0.55 * residenceFactor;
        targetTT2105 = crudeInlet + temperatureRise;

        if (flow < 10) {
          targetTT2105 += (10 - flow) * 15;
        }
        if (flow < 5) {
          targetTT2105 += (5 - flow) * 25;
        }

        targetTT2105 = Math.min(targetTT2105, 700);
      }

      const nextValue =
          TT_2105_Ref.current +
          (targetTT2105 - TT_2105_Ref.current) * 0.03;

      const rounded = Number(nextValue.toFixed(1));

      if (rounded !== TT_2105_Ref.current) {
          TT_2105_Ref.current = rounded;
          setTT_2105(rounded);
      }
    }, 1000);

    return () => {
      console.log('❌ TT_2105 interval cleared');
      if (TT_2105_intervalRef.current) {
        clearInterval(TT_2105_intervalRef.current);
        TT_2105_intervalRef.current = null;
      }
      TT_2105_isMounted.current = false;
    };
  }, []);

  const TT_2103 = TT_2102 * 1.0345;

  const TT_2107 = xv2106Open ? 145.4 : 34;
  const PT_2107 = xv2106Open ? 0.4 : 0.03;
  const TT_2108 = xv2106Open ? TT_2107 + (TT_2102 * 0.532) : TT_2107;
  const PT_2108 = xv2106Open ? PT_2107 + (TT_2102 * 0.0012) : PT_2107;
  const PT_21010 = PT_2101 > 0.04 ? (PT_2101 - 0.215) * (TT_2102 * 0.0042) : 0.03;
  const PT_2109 = xv2107Open ? PT_2108 : 0.03;
  const TT_2109 = xv2107Open ? TT_2108 : 36;
  const PT_21011 = xv2108Open ? PT_2108 : 0.03;
  const TT_21011 = xv2108Open ? TT_2108 : 36;
  const PT_21012 = TT_2102 > 50 ? (0.03 + (TT_2102 * 0.0162)) + (1 - (Damper_Opening / 50)) : 0.03;
  const PT_21013 = PT_21012 * 0.912 + (1 - (Damper_Opening / 100));
  const PT_21014 = Damper_Opening > 0 ? PT_21012 * 0.822 - (1 - (Damper_Opening / 100)) : 0.03;
  const TT_21012 = xv21010Open ? TT_2103 * 0.932 : 24;

  /* =========================================================
    BLOWER / PUMP CONTEXT
  ========================================================= */
  const pumpContext = useContext(PumpContext) || {};
  const pumpData = pumpContext.pumpData || {};

  /* =========================================================
   AIR PREHEATER TEMPERATURES
========================================================= */

const TT_21013 =
airPreheaterTemp.TT_21013;

const TT_21014 =
airPreheaterTemp.TT_21014;

const B201_Running =
  pumpData?.["B-201"]?.running ?? false;

const B202_Running =
  pumpData?.["B-202"]?.running ?? false;

const FCV2108 =
  (valveData?.FCV_2108?.opening || 0) / 100;

const FCV2109 =
  (valveData?.FCV_2109?.opening || 0) / 100;

const AirFlow =
  B201_Running
    ? FCV2108
    : 0;

const FlueGasFlow =
  B202_Running
    ? FCV2109
    : 0;




/* ==========================================================
   AIR PREHEATER TEMPERATURE CALCULATION
   LOOP PROTECTED VERSION
========================================================== */

const airPreheaterIntervalRef = useRef(null);

const airPreheaterMountedRef = useRef(false);


const ATM_Air_Ref = useRef(ATM_Air);
const TT_21012_Ref = useRef(TT_21012);
const AirFlow_Ref = useRef(AirFlow);
const FlueGasFlow_Ref = useRef(FlueGasFlow);



useEffect(()=>{
   console.log("Effect G");
    ATM_Air_Ref.current = ATM_Air;
},[ATM_Air]);


useEffect(()=>{
   console.log("Effect H");
    TT_21012_Ref.current = TT_21012;
},[TT_21012]);


useEffect(()=>{
   console.log("Effect I");
    AirFlow_Ref.current = AirFlow;
},[AirFlow]);


useEffect(()=>{
   console.log("Effect J");
    FlueGasFlow_Ref.current = FlueGasFlow;
},[FlueGasFlow]);



useEffect(()=>{
 console.log("Effect K");

if(airPreheaterMountedRef.current)
{
    return;
}


airPreheaterMountedRef.current=true;



airPreheaterIntervalRef.current=setInterval(()=>{


setAirPreheaterTemp(prev=>{


const airIn =
Number(ATM_Air_Ref.current || 25);


const flueIn =
Number(TT_21012_Ref.current || 25);



let targetAir =
airIn;


let targetFlue =
flueIn;



const exchangerActive =

AirFlow_Ref.current > 0.05 &&
FlueGasFlow_Ref.current >0.05 &&
flueIn>airIn;



if(exchangerActive)
{

const deltaT =
flueIn-airIn;


const effectiveness =
Math.min(
AirFlow_Ref.current,
FlueGasFlow_Ref.current
)
*
0.70;



const heatTransfer =
deltaT*effectiveness;



targetAir =
airIn+
(
heatTransfer*0.55
);



targetFlue =
flueIn-
(
heatTransfer*0.45
);



targetAir =
Math.min(
targetAir,
flueIn-5
);



targetFlue =
Math.max(
targetFlue,
airIn+5
);

}



const nextAir =
Number(
(
prev.TT_21014+
(
targetAir-prev.TT_21014
)
*0.08
)
.toFixed(2)
);



const nextFlue =
Number(
(
prev.TT_21013+
(
targetFlue-prev.TT_21013
)
*0.08
)
.toFixed(2)
);



if(
nextAir===prev.TT_21014 &&
nextFlue===prev.TT_21013
)
{
return prev;
}



return {

TT_21014:
nextAir,

TT_21013:
nextFlue

};



});



},1000);



return()=>{

clearInterval(
airPreheaterIntervalRef.current
);


airPreheaterMountedRef.current=false;


};


},[]);


  const TT_2104 = B202_Running ? TT_21013 * 1.03 : TT_2103 * 0.854;

  /* ==========================================================
   FURNACE PERFORMANCE REPORT
   (Read-Only Calculations)
========================================================== */

const FurnacePerformance = (() => {

    /* -----------------------------
       Fuel Flows
    ------------------------------ */

    const fuelOilFlow = FIC_2107;
    const methaneFlow = FIC_2105;
    const cngFlow = FIC_2104;
    const pilotFlow = FIC_2108;

    const totalFuelFlow =
        fuelOilFlow +
        methaneFlow +
        cngFlow +
        pilotFlow;

    /* -----------------------------
       Heat Input
    ------------------------------ */

    const fuelOilHeat =
        fuelOilFlow * 39500;

    const methaneHeat =
        methaneFlow * 50000;

    const cngHeat =
        cngFlow * 48500;

    const pilotHeat =
        pilotFlow * 45500;

    const totalHeatInput =
        fuelOilHeat +
        methaneHeat +
        cngHeat +
        pilotHeat;

    /* -----------------------------
       Combustion Efficiency
    ------------------------------ */

    let combustionEfficiency = 92;

    if (TT_2102 < 350)
        combustionEfficiency -= 12;

    if (TT_2102 < 500)
        combustionEfficiency -= 4;

    if (Damper_Opening > 80)
        combustionEfficiency -= 2;

    if (Damper_Opening < 20)
        combustionEfficiency -= 3;

    combustionEfficiency =
        Math.max(
            70,
            Math.min(
                98,
                combustionEfficiency
            )
        );

    /* -----------------------------
       Air / Fuel Ratio
    ------------------------------ */

    const airFuelRatio =
        totalFuelFlow > 0
            ? AirFlow / totalFuelFlow
            : 0;

    /* -----------------------------
       Stack Temperature
    ------------------------------ */

    const stackTemperature =
        TT_21012;

    /* -----------------------------
       Oxygen Estimate
    ------------------------------ */

    let stackOxygen =
        2 +
        (Damper_Opening / 100) * 6;

    stackOxygen =
        Math.max(
            0,
            Math.min(
                10,
                stackOxygen
            )
        );

    /* -----------------------------
       Thermal Efficiency
    ------------------------------ */

    const thermalEfficiency =
        combustionEfficiency *
        (
            TT_2105 /
            Math.max(TT_2102, 1)
        );

    /* -----------------------------
       Furnace Duty
    ------------------------------ */

    const furnaceDuty =
        totalHeatInput *
        (
            combustionEfficiency /
            100
        );

    /* -----------------------------
       Draft Condition
    ------------------------------ */

    let draftStatus = "Normal";

    if (Damper_Opening > 80)
        draftStatus = "High Draft";

    if (Damper_Opening < 20)
        draftStatus = "Low Draft";

    /* -----------------------------
       Flame Quality
    ------------------------------ */

    let flameQuality = "Good";

    if (TT_2102 < 400)
        flameQuality = "Weak";

    if (TT_2102 > 580)
        flameQuality = "Excellent";

    return {

        totalFuelFlow,

        totalHeatInput,

        furnaceDuty,

        combustionEfficiency,

        thermalEfficiency,

        airFuelRatio,

        stackTemperature,

        stackOxygen,

        flameTemperature: TT_2102,

        crudeOutletTemperature: TT_2105,

        draftStatus,

        flameQuality

    };

})();

  // ✅ Update PlantStore without causing re-renders
  useEffect(() => {
     console.log("Effect L");
    const current = PlantStore.get().transmitters;
    
    if (
      current.Damper_Opening === Damper_Opening &&
      current.FCV_2102 === FCV_2102 &&
      current.FCV_2101 === FCV_2101 &&
      current.PT_21011 === PT_21011 &&
      current.TT_21011 === TT_21011 &&
      current.PT_2109 === PT_2109 &&
      current.TT_2109 === TT_2109 &&
      current.TT_2105 === TT_2105 &&
      current.PT_21010 === PT_21010 &&
      current.FIC_2101 === FIC_2101
    ) {
      return;
    }

    PlantStore.set({
      transmitters: {
        ...current,
        Damper_Opening,
        FCV_2102,
        FCV_2101,
        PT_21011,
        TT_21011,
        PT_2109,
        TT_2109,
        TT_2105,
        PT_21010,
        FIC_2101,
      }
    });
  }, [
    Damper_Opening,
    FCV_2102,
    FCV_2101,
    PT_21011,
    TT_21011,
    PT_2109,
    TT_2109,
    TT_2105,
    PT_21010,
    FIC_2101
  ]);




  /* =========================================================
   FURNACE PERFORMANCE CALCULATIONS
========================================================= */

const crudeTemperatureRise =
    Number(TT_2105 || 0) -
    Number(TT_2101 || 0);

const steamTemperatureRise =
    Number(TT_2108 || 0) -
    Number(TT_2107 || 0);

const steamPressureDrop =
    Number(PT_2107 || 0) -
    Number(PT_2108 || 0);

/* ===============================
   Radiation Zone Status
================================ */

const radiationZoneStatus =
    TT_2102 >= 580
        ? "HIGH FIRING"
        : TT_2102 >= 500
        ? "NORMAL FIRING"
        : TT_2102 >= 300
        ? "LOW FIRING"
        : "OFF";

/* ===============================
   Furnace Draft
================================ */

const furnaceDraftStatus =
    PT_21012 > 0
        ? "POSITIVE DRAFT"
        : PT_21012 < 0
        ? "NEGATIVE DRAFT"
        : "BALANCED";

/* ===============================
   Stack Condition
================================ */

const stackCondition =
    TT_2104 <= 220
        ? "EXCELLENT"
        : TT_2104 <= 260
        ? "GOOD"
        : TT_2104 <= 320
        ? "HIGH STACK LOSS"
        : "VERY HIGH STACK LOSS";

/* ===============================
   Steam Performance
================================ */

const steamPerformance =
    steamTemperatureRise >= 180
        ? "EXCELLENT"
        : steamTemperatureRise >= 120
        ? "GOOD"
        : steamTemperatureRise >= 60
        ? "FAIR"
        : "POOR";

/* ===============================
   Fuel Status
================================ */

const fuelStatus =
(
    PT_2103 > 0.05 ||
    PT_2104 > 0.05 ||
    PT_2105 > 0.05
)
    ? "AVAILABLE"
    : "NO FUEL";

/* ===============================
   Furnace Status
================================ */

const furnaceStatus =
(
    TT_2102 > 150 &&
    (
        FIC_2104 > 0 ||
        FIC_2105 > 0 ||
        FIC_2107 > 0 ||
        FIC_2108 > 0
    )
)
? "RUNNING"
: "SHUTDOWN";

/* ===============================
   Steam Generation
================================ */

const steamGenerationStatus =
(
    TT_2108 > TT_2107 + 20
)
    ? "GENERATING"
    : "NOT GENERATING";


console.count("Furnace");

/* =========================================================
   FURNACE LABORATORY REPORT
========================================================= */
const reportTimer = useRef();

useEffect(() => {
   console.log("Effect M");

    clearTimeout(reportTimer.current);

    reportTimer.current = setTimeout(() => {

        const furnaceReport = {
            generated: new Date().toLocaleString(),
            reportNumber: "FUR-" + Date.now(),

        feed: {

            flow:
                FIC_2101,

            temperature:
                TT_2101,

            pressure:
                PT_2101

        },

        radiationZone: {

            temperature:
                TT_2102,

            bridgewallTemperature:
                TT_2103,

            draftPressure:
                PT_21012,

            damperOpening:
                Damper_Opening,

            status:
                radiationZoneStatus

        },

        convectionZone: {

            flueGasTemperature:
                TT_21013,

            flueGasPressure:
                PT_21013,

            combustionAirTemperature:
                TT_21014,

            atmosphericAir:
                ATM_Air,

            blowerB201:
                B201_Running,

            blowerB202:
                B202_Running,

            airValve:
                FCV2108,

            flueValve:
                FCV2109

        },

        airPreheater: {

    airInlet:
        ATM_Air,

    airOutlet:
        TT_21014,

    temperatureRise:
        TT_21014 - ATM_Air,

    blowerB201:
        B201_Running,

    blowerB202:
        B202_Running,

    airValve:
        FCV2108,

    flueValve:
        FCV2109

},

        stack: {

            temperature:
                TT_2104,

            pressure:
                PT_21014,

            condition:
                stackCondition,

            draftStatus:
                furnaceDraftStatus

        },

        crude: {

            inletTemperature:
                TT_2101,

            outletTemperature:
                TT_2105,

            temperatureRise:
                crudeTemperatureRise

        },

        steam: {

            inletTemperature:
                TT_2107,

            outletTemperature:
                TT_2108,

            inletPressure:
                PT_2107,

            outletPressure:
                PT_2108,

            temperatureRise:
                steamTemperatureRise,

            pressureDrop:
                steamPressureDrop,

            status:
                steamPerformance

        },

        fuel: {

            fuelGasFlow:
                FIC_2105,

            fuelGasPressure:
                PT_2103,

            CNGFlow:
                FIC_2104,

            CNGPressure:
                PT_2104,

            fuelOilFlow:
                FIC_2107,

            fuelOilPressure:
                PT_2105,

            atomizingSteamFlow:
                FIC_2108,

            atomizingSteamPressure:
                PT_2106,

            status:
                fuelStatus

        },

        performance: {

            furnaceStatus,

            steamGenerationStatus,

            radiationZoneStatus,

            stackCondition,

            furnaceDraftStatus,

            crudeTemperatureRise,

            steamTemperatureRise,

            steamPressureDrop,

            steamPerformance,

            fuelStatus

        }

    };

        localStorage.setItem(
            "FURNACE_REPORT",
            JSON.stringify(furnaceReport)
        );

    },1000);

    return ()=>clearTimeout(reportTimer.current);

},[
    FIC_2101,
    TT_2101,
    PT_2101,
    TT_2102,
    TT_2103,
    PT_21012,
    Damper_Opening,
    TT_21013,
    PT_21013,
    TT_21014,
    ATM_Air,
    B201_Running,
    B202_Running,
    FCV2108,
    FCV2109,
    TT_2104,
    PT_21014,
    TT_2105,
    TT_2107,
    TT_2108,
    PT_2107,
    PT_2108,
    PT_2103,
    PT_2104,
    PT_2105,
    PT_2106,
    FIC_2104,
    FIC_2105,
    FIC_2107,
    FIC_2108
]);



                /* 🔥 ALARM ENGINE TAGS */
        
                const { evaluateTag } = useContext(AlarmContext);
        
                useEffect(() => {
                  evaluateTag("TT_2105", TT_2105);
                  

                }, [TT_2105, ]);
                



  return (
    <div className="Furnace-container">
      {/* TITLE */}
      <h2
        style={{
          position: "absolute",
          top: "0px",
          left: "250px",
          color: "white",
          background: "rgb(30, 5, 250)",
          padding: "2px 373px",
          zIndex: 10,
        }}
      >
        FURNACE
      </h2>



      {/* =================SHUTDOWN VALVES ================= */}
      <Valve
        className="XV-2101"
        tag="XV_2101"
        x={167}
        y={617}
        isClosed={valves.XV_2101}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2102"
        tag="XV_2102"
        x={167}
        y={666}
        isClosed={valves.XV_2102}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2103"
        tag="XV_2103"
        x={167}
        y={738}
        isClosed={valves.XV_2103}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2104"
        tag="XV_2104"
        x={167}
        y={798}
        isClosed={valves.XV_2104}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2105"
        tag="XV_2105"
        x={167}
        y={859}
        isClosed={valves.XV_2105}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2106"
        tag="XV_2106"
        x={177}
        y={152}
        isClosed={valves.XV_2106}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2107"
        tag="XV_2107"
        x={1157}
        y={172}
        isClosed={valves.XV_2107}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2108"
        tag="XV_2108"
        x={1157}
        y={222}
        isClosed={valves.XV_2108}
        toggle={toggleValve}
      />

      <Valve
        className="XV-2109"
        tag="XV_2109"
        x={1107}
        y={278}
        isClosed={valves.XV_2109}
        toggle={toggleValve}
      />

      <Valve
        className="XV-21010"
        tag="XV_21010"
        x={727}
        y={239}
        isClosed={valves.XV_21010}
        toggle={toggleValve}
      />

      {renderInput({
        name: "Damper Opening",
        value: Damper_Opening,
        setter: setDamper_Opening,
        x: 530,
        y: 230,
        bg: "#d7f1d7",
        w: 50,
        h: 20,
      })}

      {renderInput({
        name: "Atmospheric Air",
        value: ATM_Air,
        setter: setATM_Air,
        x: 1250,
        y: 340,
        bg: "#bde7bd",
        w: 50,
        h: 20,
      })}

      <Damper
        tag="DAM_12001"
        x={582}
        y={190}
        width={95}
        height={2}
        opening={Damper_Opening}
      />

      <SparkButton
        spark={spark}
        setSpark={setSpark}
        x={850}
        y={800}
        width={90}
        height={35}
      />

      <PilotFlame
        value={PT_2102}
        spark={spark}
        setSpark={setSpark}
        x={654}
        y={456}
        width={30}
        maxHeight={100}
      />

      <MainGasFlame
        pilotOn={spark}
        PT_2104={PT_2104}
        PT_2103={PT_2103}
        x={528}
        y={434}
        width={190}
        height={120}
      />

      <FuelOilFlame
        PT_2105={PT_2105}
        PT_2106={PT_2106}
        x={532}
        y={430}
        width={190}
        height={120}
      />

      <FlueGas
        x={596}
        y={140}
        width={60}
        height={340}
        Damper_opening={Damper_Opening}
      />

      <StackFlueGas
        x={596}
        y={-160}
        width={60}
        height={540}
        Damper_opening={Damper_Opening}
      />





      {/* ================= CONTROL VALVES SVG ================= */}
      {/* FCV-2101 */}
      <div
        style={{ position: "absolute", left: 412, top: 126, transform: "rotate(90deg)", transformOrigin: "center" }}
        onMouseEnter={() => setHoveredValve("FCV_2101")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2101")}
      >
        {hoveredValve === "FCV_2101" && (
          <div style={tagStyle}>FCV_2101</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2101">
          <defs>
            <linearGradient id="metal_2101">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2101)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2101)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* FCV-2102 */}
      <div
        style={{ position: "absolute", left: 766, top: 116, transform: "rotate(270deg)", transformOrigin: "center" }}
        onMouseEnter={() => setHoveredValve("FCV_2102")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2102")}
      >
        {hoveredValve === "FCV_2102" && (
          <div style={tagStyle}>FCV_2102</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2102">
          <defs>
            <linearGradient id="metal_2102">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2102)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2102)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* FCV-2103 */}
      <div
        style={{ position: "absolute", left: 410, top: 609 }}
        onMouseEnter={() => setHoveredValve("FCV_2103")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2103")}
      >
        {hoveredValve === "FCV_2103" && (
          <div style={tagStyle}>FCV_2103</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2103">
          <defs>
            <linearGradient id="metal_2103">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2103)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2103)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* FCV-2104 */}
      <div
        style={{ position: "absolute", left: 450, top: 658 }}
        onMouseEnter={() => setHoveredValve("FCV_2104")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2104")}
      >
        {hoveredValve === "FCV_2104" && (
          <div style={tagStyle}>FCV_2104</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2104">
          <defs>
            <linearGradient id="metal_2104">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2104)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2104)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* FCV-2105 */}
      <div
        style={{ position: "absolute", left: 400, top: 730 }}
        onMouseEnter={() => setHoveredValve("FCV_2105")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2105")}
      >
        {hoveredValve === "FCV_2105" && (
          <div style={tagStyle}>FCV_2105</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2105">
          <defs>
            <linearGradient id="metal_2105">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2105)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2105)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* FCV-2106 */}
      <div
        style={{ position: "absolute", left: 450, top: 790 }}
        onMouseEnter={() => setHoveredValve("FCV_2106")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2106")}
      >
        {hoveredValve === "FCV_2106" && (
          <div style={tagStyle}>FCV_2106</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2106">
          <defs>
            <linearGradient id="metal_2106">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2106)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2106)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* FCV-2107 */}
      <div
        style={{ position: "absolute", left: 400, top: 850 }}
        onMouseEnter={() => setHoveredValve("FCV_2107")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2107")}
      >
        {hoveredValve === "FCV_2107" && (
          <div style={tagStyle}>FCV_2107</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2107">
          <defs>
            <linearGradient id="metal_2107">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2107)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2107)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* FCV-2108 */}
      <div
        style={{ position: "absolute", left: 800, top: 552 }}
        onMouseEnter={() => setHoveredValve("FCV_2108")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2108")}
      >
        {hoveredValve === "FCV_2108" && (
          <div style={tagStyle}>FCV_2108</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2108">
          <defs>
            <linearGradient id="metal_2108">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2108)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2108)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* FCV-2109 */}
      <div
        style={{ position: "absolute", left: 1214, top: 530, transform: "rotate(90deg)", transformOrigin: "center" }}
        onMouseEnter={() => setHoveredValve("FCV_2109")}
        onMouseLeave={() => setHoveredValve(null)}
        onClick={() => openPanel("FCV_2109")}
      >
        {hoveredValve === "FCV_2109" && (
          <div style={tagStyle}>FCV_2109</div>
        )}
        <svg width="80" height="50" viewBox="0 0 200 220" className="FCV-2109">
          <defs>
            <linearGradient id="metal_2109">
              <stop offset="0%" stopColor="#bdc3c7" />
              <stop offset="50%" stopColor="#ecf0f1" />
              <stop offset="100%" stopColor="#7f8c8d" />
            </linearGradient>
          </defs>
          <path d="M20 60 A80 60 0 0 1 180 60 L180 90 L20 90 Z" fill="#eb2a11" stroke="#3498db" strokeWidth="2" />
          <rect x="95" y="90" width="10" height="70" fill="#3d5f2a" />
          <polygon points="20,200 100,150 20,110" fill="url(#metal_2109)" stroke="#7f8c8d" strokeWidth="2" />
          <polygon points="180,200 100,150 180,110" fill="url(#metal_2109)" stroke="#7f8c8d" strokeWidth="2" />
        </svg>
      </div>

      {/* ================= CONTROL VALVE FACEPLATES ================= */}
      {openPanels.map((tag, index) => (
        <ControlValveControlPanel
          key={tag}
          tag={tag}
          data={valveData?.[tag] ?? DEFAULT_VALVES?.[tag]}
          update={updateValveData}
          close={() => closePanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= PUMPS CONTROL VALVE FACEPLATES ================= */}
      {openPumpPanels.map((tag, index) => (
        <PumpFaceplate
          key={tag}
          tag={tag}
          close={() => closePumpPanel(tag)}
          defaultPosition={{
            x: 300 + index * 40,
            y: 150 + index * 40
          }}
        />
      ))}

      {/* ================= CONTROL VALVE OUTPUTS ================= */}
      <ValveOutput
        value={Number(valveData?.FCV_2101?.opening || 0)}
        x={395}
        y={144}
      />

      <ValveOutput
        value={Number(valveData?.FCV_2102?.opening || 0)}
        x={819}
        y={132}
      />

      <ValveOutput
        value={Number(valveData?.FCV_2103?.opening || 0)}
        x={432}
        y={653}
      />

      <ValveOutput
        value={Number(valveData?.FCV_2104?.opening || 0)}
        x={471}
        y={702}
      />

      <ValveOutput
        value={Number(valveData?.FCV_2105?.opening || 0)}
        x={421}
        y={774}
      />

      <ValveOutput
        value={Number(valveData?.FCV_2106?.opening || 0)}
        x={471}
        y={835}
      />

      <ValveOutput
        value={Number(valveData?.FCV_2107?.opening || 0)}
        x={420}
        y={895}
      />

      <ValveOutput
        value={Number(valveData?.FCV_2108?.opening || 0)}
        x={822}
        y={599}
      />

      <ValveOutput
        value={Number(valveData?.FCV_2109?.opening || 0)}
        x={1202}
        y={547}
      />

      <Transmitter
        tag="TT_2101"
        value={[TT_2101]}
        x={142}
        y={76}
        unit="°C"
        max={300}
        HH={286}
        H={265}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE FROM E-111A/B TO F-101"
      />

      <Transmitter
        tag="PT_2101"
        value={[PT_2101]}
        x={242}
        y={76}
        unit="MPa"
        max={3}
        HH={2.8}
        H={1.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF FLASHED CRUDE FROM BOTTOM OF PREFLASH TOWER TO FURNACE (F-101) AT THE CONVECTION ZONE 1 & 2"
      />

      <Transmitter
        tag="FIC_2101"
        value={[FIC_2101]}
        x={332}
        y={76}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF FLASHED CRUDE FROM BOTTOM OF PREFLASH TOWER TO FURNACE (F-101) AT THE CONVECTION ZONE 1 & 2"
      />

      <Transmitter
        tag="FIC_2102"
        value={[FIC_2102]}
        x={452}
        y={182}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF FLASHED CRUDE FROM BOTTOM OF PREFLASH TOWER TO FURNACE (F-101) AT THE CONVECTION ZONE 1"
      />

      <Transmitter
        tag="FIC_2103"
        value={[FIC_2103]}
        x={642}
        y={76}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF FLASHED CRUDE FROM BOTTOM OF PREFLASH TOWER TO FURNACE (F-101) AT THE CONVECTION ZONE 2"
      />

      <Transmitter
        tag="FIC_2104"
        value={[FIC_2104]}
        x={225}
        y={846}
        unit="SCF/M"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF LOW PRESSURE GAS FROM T-101 REFLUX DRUM TO PILOT BURNER OF FURNACE"
      />

      <Transmitter
        tag="PT_2102"
        value={[PT_2102]}
        x={323}
        y={846}
        unit="MPa"
        max={3}
        HH={2.8}
        H={1.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF LOW PRESSURE GAS FROM T-101 REFLUX DRUM TO PILOT BURNER OF FURNACE"
      />

      <Transmitter
        tag="FIC_2105"
        value={[FIC_2105]}
        x={225}
        y={776}
        unit="SCF/M"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF HIGH PRESSURE GAS FROM T-102 REFLUX DRUM TO MAIN BURNER OF FURNACE"
      />

      <Transmitter
        tag="PT_2103"
        value={[PT_2103]}
        x={323}
        y={776}
        unit="MPa"
        max={3}
        HH={2.8}
        H={1.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF HIGH PRESSURE GAS FROM T-102 REFLUX DRUM TO MAIN BURNER OF FURNACE"
      />

      <Transmitter
        tag="FIC_2106"
        value={[FIC_2106]}
        x={225}
        y={726}
        unit="SCF/M"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF CNG GAS FROM EXTERNAL SOURCES TO MAIN BURNER OF FURNACE"
      />

      <Transmitter
        tag="PT_2104"
        value={[PT_2104]}
        x={323}
        y={726}
        unit="MPa"
        max={3}
        HH={2.8}
        H={1.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF CNG GAS FROM EXTERNAL SOURCES TO MAIN BURNER OF FURNACE"
      />

      <Transmitter
        tag="FIC_2107"
        value={[FIC_2107]}
        x={225}
        y={656}
        unit="BPM"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF NAPHTHA/FUEL OIL FROM FROM FUEL OIL TANK TO MAIN BURNER OF FURNACE"
      />

      <Transmitter
        tag="PT_2105"
        value={[PT_2105]}
        x={323}
        y={656}
        unit="MPa"
        max={3}
        HH={2.8}
        H={1.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF NAPHTHA/FUEL OIL FROM FROM FUEL OIL TANK TO MAIN BURNER OF FURNACE"
      />

      <Transmitter
        tag="FIC_2108"
        value={[FIC_2108]}
        x={225}
        y={606}
        unit="SCF/M"
        max={35}
        HH={34}
        H={30}
        L={0}
        LL={0}
        c="lime"
        description="FLOWRATE OF INSTRUMENT AIR FOR ATMOIZING FUEL OIL/NAPHTHA AT MAIN BURNER"
      />

      <Transmitter
        tag="PT_2106"
        value={[PT_2106]}
        x={323}
        y={606}
        unit="MPa"
        max={3}
        HH={2.8}
        H={1.5}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF INSTRUMENT AIR FOR ATMOIZING FUEL OIL/NAPHTHA AT MAIN BURNER"
      />

      <Transmitter
        tag="TT_2102"
        value={[TT_2102]}
        x={462}
        y={516}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF BODY OF FURNACE AT THE RADIATION ZONE"
      />

      <Transmitter
        tag="TT_2103"
        value={[TT_2103]}
        x={492}
        y={316}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF BODY OF FURNACE AT THE CONVECTION ZONE"
      />

      <Transmitter
        tag="TT_2104"
        value={[TT_2104]}
        x={662}
        y={156}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF BODY OF FURNACE AT THE CONVECTION ZONE"
      />

      <Transmitter
        tag="TT_2105"
        value={[TT_2105]}
        x={962}
        y={116}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF CRUDE OUTLET FROM THE FURNACE (F-101) TO MAIN ATMOSPHERIC DISTILLATION TOWER (T-102)"
      />

      <Transmitter
        tag="PT_21010"
        value={[PT_21010]}
        x={1062}
        y={116}
        unit="MPa"
        max={2}
        HH={1.5}
        H={0.8}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF CRUDE OUTLET FROM THE FURNACE (F-101) TO MAIN ATMOSPHERIC DISTILLATION TOWER (T-102)"
      />

      <Transmitter
        tag="TT_2107"
        value={[TT_2107]}
        x={262}
        y={138}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF 0.4MPa STEAM FROM THE STEAM SEPARATOR (V-506) TO THE CONVECTION ZONE OF FURNACE"
      />

      <Transmitter
        tag="PT_2107"
        value={[PT_2107]}
        x={322}
        y={316}
        unit="MPa"
        max={2}
        HH={1.5}
        H={0.8}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF 0.4MPa STEAM FROM THE STEAM SEPARATOR (V-506) TO THE CONVECTION ZONE OF FURNACE"
      />

      <Transmitter
        tag="TT_2108"
        value={[TT_2108]}
        x={882}
        y={162}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF SUPERHEATED STEAM FROM THE CONVECTION ZONE OF FURNACE TO T-102 AND T-103"
      />

      <Transmitter
        tag="PT_2108"
        value={[PT_2108]}
        x={992}
        y={162}
        unit="MPa"
        max={2}
        HH={1.5}
        H={0.8}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF SUPERHEATED STEAM FROM THE CONVECTION ZONE OF FURNACE TO T-102 AND T-103"
      />

      <Transmitter
        tag="PT_21012"
        value={[PT_21012]}
        x={372}
        y={514}
        unit="kPa"
        max={12}
        HH={11}
        H={8}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF RADIATION ZONE OF FURNACE"
      />

      <Transmitter
        tag="PT_21013"
        value={[PT_21013]}
        x={491}
        y={280}
        unit="kPa"
        max={12}
        HH={11}
        H={8}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF CONVECTION ZONE OF FURNACE"
      />

      <Transmitter
        tag="PT_21014"
        value={[PT_21014]}
        x={511}
        y={130}
        unit="kPa"
        max={12}
        HH={11}
        H={8}
        L={0}
        LL={0}
        c="lime"
        description="PRESSURE OF STACK AT THE FURNACE"
      />

      <Transmitter
        tag="TT_21012"
        value={[TT_21012]}
        x={879}
        y={412}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FLUE GAS FROM STACK BEFORE THE DAMPER VALVE TO AIR PREHEATER"
      />

      <Transmitter
        tag="TT_21013"
        value={[TT_21013]}
        x={1249}
        y={482}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF FLUE GAS FROM AIR PREHEATER THROUGH INDUCED DRAFT FAN TO FLARE STACK AFTER THE DAMPER"
      />

      <Transmitter
        tag="TT_21014"
        value={[TT_21014]}
        x={809}
        y={642}
        unit="°C"
        max={700}
        HH={586}
        H={565}
        L={0}
        LL={0}
        c="lime"
        description="TEMPERATURE OF HOT AIR FROM AIR PREHEATER THROUGH AIR BLOWER TO AIR REGISTER AT THE BOTTOM OF FLARE"
      />

      <Pump
        tag="B-201"
        x={1001}
        y={627}
        rpm={1800}
        bladeCount={pumpData?.["B-201"]?.bladeCount || 3}
        size={pumpData?.["B-201"]?.size || 43}
        onClick={openPumpPanel}
      />

      <Pump
        tag="B-202"
        x={1144}
        y={654}
        rpm={1800}
        bladeCount={pumpData?.["B-202"]?.bladeCount || 3}
        size={pumpData?.["B-202"]?.size || 43}
        onClick={openPumpPanel}
      />
    </div>
  );
}

export default memo(Furnace);